// React and related imports
import React, { useEffect, useCallback } from 'react';

// Flowbite components
import { Button, Modal } from 'flowbite-react';

// i18 hooks
import { useTranslation } from 'react-i18next';

// RTK Query Hooks
import {
  useCreateSessionMutation,
  useUpdateSessionMutation,
} from '../../redux/api/sessionsApi';

// Redux selectors, hooks, and actions
import { useAppDispatch } from '../../redux/hooks/useAppDispatch';
import { useAppSelector } from '../../redux/hooks/useAppSelector';
import {
  clearAllValidationErrors,
  clearSelectedSession,
  clearValidationErrors,
  closeSessionModal,
  setValidationErrors,
} from '../../redux/slices/sessionSlice';
import {
  selectFormHasErrors,
  selectIsSessionModalOpenedForAdd,
  selectSelectedSession,
  selectValidationErrors,
} from '../../redux/selectors/sessionSelectors';
import { selectIsDarkMode } from '../../redux/selectors/displaySettingsSelectors';

// Types and Enums
import { SessionAssignTo } from '../../common/enums/SessionAssignTo';

// Custom Components
import { MultiLangSessionInputs } from './MultiLangSessionInputs/MultiLangSessionInputs';
import { SessionUrlInput } from './SessionUrlInput/SessionUrlInput';
import { SessionUserAssignment } from './SessionUserAssignment/SessionUserAssignment';
import { SessionStatusToggle } from './SessionStatusToggle/SessionStatusToggle';
import { SessionValidationAlert } from './SessionValidationAlert/SessionValidationAlert';

// Custom Hooks
import { useSessionState } from '../../hooks/useSessionState';
import { useFormChanges } from '../../hooks/useFormChanges';
import { usePreventUnload } from '../../hooks/usePreventUnload';
import { usePagination } from '../../hooks/usePagination';

// Utils
import { checkFieldValidity } from '../../utils/checkFieldValidity';
import { SessionState } from '../../hooks/useSessionState';

export const SessionModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const { setCurrentPage } = usePagination();

  const isDarkMode = useAppSelector(selectIsDarkMode);
  const hasErrors = useAppSelector(selectFormHasErrors);
  const validationErrors = useAppSelector(selectValidationErrors);

  // Collect errors in specific order: title, description, url, userIds
  const formErrors = [
    ...(validationErrors.title || []),
    ...(validationErrors.description || []),
    ...(validationErrors.url || []),
    ...(validationErrors.userIds || []),
  ];

  const selectedSession = useAppSelector(selectSelectedSession);
  const isModalForAdd = useAppSelector(selectIsSessionModalOpenedForAdd);
  const [updateSession, { isLoading }] = useUpdateSessionMutation();
  const [createSession, { isLoading: creating }] = useCreateSessionMutation();

  const { isModalOpen, sessionState, initialSessionSnapshot, updateField } =
    useSessionState();

  const isLoadingState = isLoading || creating;
  const buttonClass = `btn ${isDarkMode ? 'btn-dark' : 'btn-light'}`;

  const { hasChanges, hasChangesRef, resetInitialValues } =
    useFormChanges<SessionState>({
      initialValues: initialSessionSnapshot as SessionState,
      currentValues: sessionState as SessionState,
    });

  const validateField = useCallback(
    (field: string, value: string | undefined = undefined) => {
      // Use current sessionState value if no value provided
      const valueToValidate =
        value !== undefined
          ? value
          : (sessionState?.[field as keyof SessionState] as string) || '';

      // Special validation for userIds when assignTo is Specific
      if (
        field === 'userIds' &&
        sessionState?.assignTo === SessionAssignTo.Specific
      ) {
        if (sessionState.userIds.length === 0) {
          dispatch(
            setValidationErrors({
              field: 'userIds',
              errors: ['userSelectionRequired'],
            })
          );
          return;
        } else {
          dispatch(clearValidationErrors('userIds'));
          return;
        }
      }

      const errors = checkFieldValidity(field, valueToValidate);
      if (errors.length > 0) {
        dispatch(setValidationErrors({ field, errors }));
      } else {
        dispatch(clearValidationErrors(field));
      }
    },
    [sessionState?.assignTo, sessionState?.userIds, sessionState, dispatch]
  );

  usePreventUnload({
    shouldPrevent: hasChanges && isModalOpen,
  });

  // Validate required fields when modal opens in add mode
  useEffect(() => {
    if (isModalForAdd && sessionState) {
      validateField('url', sessionState.url);
    }
  }, [isModalForAdd, validateField, sessionState?.url]);

  // Auto-validate userIds when assignTo is Specific
  useEffect(() => {
    if (sessionState?.assignTo === SessionAssignTo.Specific) {
      if (sessionState.userIds.length === 0) {
        dispatch(
          setValidationErrors({
            field: 'userIds',
            errors: ['userSelectionRequired'],
          })
        );
      } else {
        dispatch(clearValidationErrors('userIds'));
      }
    } else {
      // Clear validation errors when not in Specific mode
      dispatch(clearValidationErrors('userIds'));
    }
  }, [sessionState?.assignTo, sessionState?.userIds, dispatch]);

  const onCloseModal = () => {
    if (hasChangesRef.current) {
      const shouldClose = window.confirm(
        t('sessions.unsavedChangesCloseConfirm')
      );
      if (!shouldClose) return;
    }

    dispatch(clearSelectedSession());
    dispatch(closeSessionModal());
    dispatch(clearAllValidationErrors());
  };

  const handleCreate = async () => {
    try {
      if (sessionState) {
        await createSession({
          title: sessionState.title,
          description: sessionState.description,
          url: sessionState.url,
          status: sessionState.status,
          assignTo: sessionState.assignTo,
          userIds:
            sessionState.assignTo === SessionAssignTo.Specific
              ? sessionState.userIds
              : undefined,
        }).unwrap();
        resetInitialValues(onCloseModal);
        setCurrentPage(1);
      }
    } catch (err) {
      // Error handled by middleware
      // Re-validate all fields with current values after failed creation
      if (sessionState) {
        validateField('url', sessionState.url);
        if (sessionState.assignTo === SessionAssignTo.Specific) {
          validateField('userIds');
        }
      }
    }
  };

  const handleSaveEdits = async () => {
    try {
      if (sessionState && selectedSession?.id) {
        await updateSession({
          id: selectedSession.id,
          updatedSession: {
            title: sessionState.title,
            description: sessionState.description,
            url: sessionState.url,
            status: sessionState.status,
          },
        }).unwrap();
        resetInitialValues(onCloseModal);
        setCurrentPage(1);
      }
    } catch (err) {
      // Error handled by middleware
      // Re-validate all fields with current values after failed update
      if (sessionState) {
        validateField('url', sessionState.url);
      }
    }
  };

  // Don't render modal if sessionState is not ready
  if (!sessionState) {
    return null;
  }

  return (
    <Modal
      dismissible
      show={isModalOpen}
      size="5xl"
      onClose={onCloseModal}
      className="dark:bg-primary-100"
    >
      <Modal.Header className="modal-header">
        <div className="flex items-center">
          <span className="me-3">
            {isModalForAdd
              ? t('sessions.addSession')
              : t('sessions.editSession')}
          </span>
        </div>
      </Modal.Header>

      {/* Validation Errors Alert */}
      <SessionValidationAlert
        t={t}
        errors={formErrors}
        isModalForAdd={isModalForAdd}
      />

      <Modal.Body className="space-y-5">
        {/* Multi-language input */}
        <MultiLangSessionInputs
          t={t}
          sessionState={sessionState}
          updateField={(field, value) =>
            updateField(field as keyof SessionState, value)
          }
        />

        {/* URL input */}
        <SessionUrlInput
          url={sessionState.url}
          onUrlChange={(url) => updateField('url', url)}
          onValidation={validateField}
        />

        {/* User Assignment - Only show during create mode */}
        {isModalForAdd && (
          <SessionUserAssignment
            assignTo={sessionState.assignTo}
            userIds={sessionState.userIds}
            onAssignToChange={(assignTo) => updateField('assignTo', assignTo)}
            onUserIdsChange={(userIds) => updateField('userIds', userIds)}
          />
        )}

        {/* Status toggle */}
        <SessionStatusToggle
          status={sessionState.status}
          onStatusChange={(status) => updateField('status', status)}
        />
      </Modal.Body>

      <Modal.Footer>
        <Button
          type="button"
          className={buttonClass}
          onClick={isModalForAdd ? handleCreate : handleSaveEdits}
          disabled={
            (!hasChanges && !isModalForAdd) || hasErrors || isLoadingState
          }
        >
          {t('sessions.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
